import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';

/**
 * Cancelación de un trabajo (Fase 2): decisión del admin/dueño con motivo
 * obligatorio (cliente no llegó, reprogramación imposible, error de carga…).
 * No es un cierre del técnico: no genera venta, ni encuesta, ni comisión.
 */
export function CancelarJobModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setMotivo('');
    setError(null);
    onClose();
  }

  const cancel = useMutation({
    mutationFn: () => jobsApi.cancelJob(job!.id, motivo.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Trabajo cancelado');
      close();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo cancelar el trabajo'),
  });

  function submit() {
    if (!motivo.trim()) {
      setError('El motivo es obligatorio');
      return;
    }
    setError(null);
    cancel.mutate();
  }

  const cliente =
    job?.contact?.name ?? job?.contact?.pushName ?? job?.contact?.phone ?? 'el cliente';

  return (
    <Modal
      open={Boolean(job)}
      onClose={close}
      title="Cancelar trabajo"
      size="sm"
      description={`El trabajo de ${cliente} quedará CANCELADO: no cuenta como venta ni dispara encuesta. Para mover la cita, edite la ficha en vez de cancelar.`}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={close}>
            Volver
          </Button>
          <Button variant="danger" size="sm" onClick={submit} loading={cancel.isPending}>
            Cancelar trabajo
          </Button>
        </>
      }
    >
      <Textarea
        label="Motivo de la cancelación"
        placeholder="Cliente no se presentó, pidió cancelar…"
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        error={error ?? undefined}
      />
      {/* S-15 · Con el técnico ya en el sitio, la cancelación es válida (el
          dueño no le cobra al cliente) pero el viaje se hizo. Se dice acá para
          que nadie se sorprenda de verlo contado en el reporte de costos. */}
      {job?.estado === 'EN_SITIO' && (
        <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
          El técnico ya está en el sitio. La cancelación se registra igual y al cliente no se le
          cobra nada, pero el viaje queda contado en el reporte de costos.
        </p>
      )}
    </Modal>
  );
}
